import { Component } from 'react'
import { toast } from 'react-toastify'
import { HiDeviceMobile, HiRefresh, HiTrash, HiClipboardCopy, HiDownload } from 'react-icons/hi'
import { appRef } from '../../app'
import { getString } from '../../locale'
import { Files, MyFile } from '../../components/files'
import { deleteMobileApp, IMobileAppRelease, listMobileApps, updateMobileAppTitle, uploadMobileApp } from '../../api/mobile-apps'

type State = {
    loading: boolean
    uploadingAndroid: boolean
    uploadingIos: boolean
    releases: IMobileAppRelease[]
    editingTitles: Record<string, string>
    savingTitles: Record<string, boolean>
    androidFile?: MyFile
    androidTitle: string
    androidVersion: string
    androidNotes: string
    iosFile?: MyFile
    iosTitle: string
    iosVersion: string
    iosBundleId: string
    iosNotes: string
}

function formatBytes(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${bytes} B`
}

export class MobileAppsPage extends Component<{}, State> {
    state: State = {
        loading: true,
        uploadingAndroid: false,
        uploadingIos: false,
        releases: [],
        editingTitles: {},
        savingTitles: {},
        androidTitle: '',
        androidVersion: '',
        androidNotes: '',
        iosVersion: '1.0.0',
        iosTitle: '',
        iosBundleId: 'com.example.app',
        iosNotes: '',
    }

    componentDidMount = async () => {
        appRef.setHeader(<HiDeviceMobile className="me-2" size={28} />, () => getString('pagelabels.mobile_apps'))
        await this.load()
    }

    load = async () => {
        this.setState({ loading: true })
        try {
            const response = await listMobileApps()
            this.setState({ releases: response.data })
        } catch (error: any) {
            toast.error(error?.message || getString('mobile_apps.load_failed'))
        } finally {
            this.setState({ loading: false })
        }
    }

    uploadAndroid = async () => {
        const file = this.state.androidFile
        if (!file) {
            toast.error(getString('mobile_apps.pick_apk_first'))
            return
        }

        this.setState({ uploadingAndroid: true })
        try {
            await uploadMobileApp({
                platform: 'android',
                file,
                title: this.state.androidTitle,
                version: this.state.androidVersion,
                notes: this.state.androidNotes,
            })
            toast.success(getString('mobile_apps.upload_success'))
            this.setState({ androidFile: undefined, androidTitle: '', androidVersion: '', androidNotes: '' })
            await this.load()
        } catch (error: any) {
            toast.error(error?.message || getString('mobile_apps.upload_failed'))
        } finally {
            this.setState({ uploadingAndroid: false })
        }
    }

    uploadIos = async () => {
        const file = this.state.iosFile
        if (!file) {
            toast.error(getString('mobile_apps.pick_ipa_first'))
            return
        }

        this.setState({ uploadingIos: true })
        try {
            await uploadMobileApp({
                platform: 'ios-adhoc',
                file,
                title: this.state.iosTitle,
                version: this.state.iosVersion,
                bundleId: this.state.iosBundleId,
                notes: this.state.iosNotes,
            })
            toast.success(getString('mobile_apps.upload_success'))
            this.setState({
                iosFile: undefined,
                iosTitle: '',
                iosVersion: '1.0.0',
                iosBundleId: 'com.example.app',
                iosNotes: '',
            })
            await this.load()
        } catch (error: any) {
            toast.error(error?.message || getString('mobile_apps.upload_failed'))
        } finally {
            this.setState({ uploadingIos: false })
        }
    }

    removeRelease = async (id: string) => {
        if (!window.confirm(getString('mobile_apps.delete_confirm'))) return
        try {
            await deleteMobileApp(id)
            toast.success(getString('common.deleted'))
            await this.load()
        } catch (error: any) {
            toast.error(error?.message || getString('mobile_apps.delete_failed'))
        }
    }

    startEditTitle = (release: IMobileAppRelease) => {
        this.setState(prev => ({
            editingTitles: {
                ...prev.editingTitles,
                [release.id]: release.title || '',
            },
        }))
    }

    cancelEditTitle = (id: string) => {
        this.setState(prev => {
            const next = { ...prev.editingTitles }
            delete next[id]
            return { editingTitles: next }
        })
    }

    saveTitle = async (release: IMobileAppRelease) => {
        const current = `${this.state.editingTitles[release.id] || ''}`.trim()
        if (!current) {
            toast.error(getString('mobile_apps.title_required'))
            return
        }
        this.setState(prev => ({
            savingTitles: {
                ...prev.savingTitles,
                [release.id]: true,
            },
        }))
        try {
            const response = await updateMobileAppTitle(release.id, current)
            const updated = response.data
            this.setState(prev => ({
                releases: prev.releases.map(item => (item.id === updated.id ? updated : item)),
            }))
            this.cancelEditTitle(release.id)
            toast.success(getString('mobile_apps.title_updated'))
        } catch (error: any) {
            toast.error(error?.message || getString('mobile_apps.title_update_failed'))
        } finally {
            this.setState(prev => ({
                savingTitles: {
                    ...prev.savingTitles,
                    [release.id]: false,
                },
            }))
        }
    }

    copy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text)
            toast.success(getString('mobile_apps.copied'))
        } catch {
            const textArea = document.createElement('textarea')
            textArea.value = text
            textArea.style.position = 'fixed'
            textArea.style.top = '-9999px'
            textArea.style.left = '-9999px'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()
            let copied = false
            try {
                copied = document.execCommand('copy')
            } catch {
                copied = false
            } finally {
                document.body.removeChild(textArea)
            }

            if (copied) {
                toast.success(getString('mobile_apps.copied'))
            } else {
                toast.error(getString('mobile_apps.copy_failed'))
            }
        }
    }

    render() {
        return (
            <div className="space-y-6">
                <div className="rounded-xl bg-white dark:bg-gray-800 shadow p-6">
                    <h2 className="text-xl font-semibold dark:text-white mb-1">{getString('mobile_apps.title')}</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{getString('mobile_apps.desc')}</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {this.renderAndroidCard()}
                    {this.renderIosCard()}
                </div>

                <div className="rounded-xl bg-white dark:bg-gray-800 shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold dark:text-white">{getString('mobile_apps.releases')}</h3>
                        <button onClick={this.load} className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-2">
                            <HiRefresh /> {getString('license.refresh')}
                        </button>
                    </div>
                    {this.renderList()}
                </div>
            </div>
        )
    }

    private renderAndroidCard() {
        return (
            <div className="rounded-xl bg-white dark:bg-gray-800 shadow p-6 space-y-3">
                <h3 className="text-lg font-semibold dark:text-white">{getString('mobile_apps.android_title')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{getString('mobile_apps.android_desc')}</p>

                <Files
                    multiple={false}
                    clickable
                    accepts={['.apk']}
                    onChange={files => this.setState({ androidFile: files[0] })}
                    onError={() => {}}
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-sm text-gray-600 dark:text-gray-300"
                >
                    <div>{this.state.androidFile ? this.state.androidFile.name : getString('mobile_apps.drop_apk')}</div>
                </Files>

                <input
                    value={this.state.androidTitle}
                    onChange={e => this.setState({ androidTitle: e.target.value })}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                    placeholder={getString('mobile_apps.app_title_placeholder')}
                />
                <input
                    value={this.state.androidVersion}
                    onChange={e => this.setState({ androidVersion: e.target.value })}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                    placeholder={getString('mobile_apps.version_placeholder')}
                />
                <textarea
                    value={this.state.androidNotes}
                    onChange={e => this.setState({ androidNotes: e.target.value })}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white min-h-[72px]"
                    placeholder={getString('mobile_apps.notes_placeholder')}
                />

                <button
                    onClick={this.uploadAndroid}
                    disabled={this.state.uploadingAndroid}
                    className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                    {this.state.uploadingAndroid ? getString('common.loading') : getString('mobile_apps.upload_apk')}
                </button>
            </div>
        )
    }

    private renderIosCard() {
        return (
            <div className="rounded-xl bg-white dark:bg-gray-800 shadow p-6 space-y-3">
                <h3 className="text-lg font-semibold dark:text-white">{getString('mobile_apps.ios_title')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{getString('mobile_apps.ios_desc')}</p>

                <Files
                    multiple={false}
                    clickable
                    accepts={['.ipa']}
                    onChange={files => this.setState({ iosFile: files[0] })}
                    onError={() => {}}
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-sm text-gray-600 dark:text-gray-300"
                >
                    <div>{this.state.iosFile ? this.state.iosFile.name : getString('mobile_apps.drop_ipa')}</div>
                </Files>

                <input
                    value={this.state.iosTitle}
                    onChange={e => this.setState({ iosTitle: e.target.value })}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                    placeholder={getString('mobile_apps.ios_title_placeholder')}
                />
                <input
                    value={this.state.iosVersion}
                    onChange={e => this.setState({ iosVersion: e.target.value })}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                    placeholder={getString('mobile_apps.version_placeholder')}
                />
                <input
                    value={this.state.iosBundleId}
                    onChange={e => this.setState({ iosBundleId: e.target.value })}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                    placeholder={getString('mobile_apps.bundle_placeholder')}
                />
                <textarea
                    value={this.state.iosNotes}
                    onChange={e => this.setState({ iosNotes: e.target.value })}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white min-h-[72px]"
                    placeholder={getString('mobile_apps.notes_placeholder')}
                />

                <button
                    onClick={this.uploadIos}
                    disabled={this.state.uploadingIos}
                    className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                    {this.state.uploadingIos ? getString('common.loading') : getString('mobile_apps.upload_ipa')}
                </button>
            </div>
        )
    }

    private renderList() {
        if (this.state.loading) {
            return <div className="text-gray-600 dark:text-gray-300">{getString('common.loading')}...</div>
        }
        if (this.state.releases.length === 0) {
            return <div className="text-gray-600 dark:text-gray-300">{getString('mobile_apps.empty')}</div>
        }

        return (
            <div className="space-y-3">
                {this.state.releases.map(release => (
                    <div key={release.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                {this.state.editingTitles[release.id] !== undefined ? (
                                    <div className="flex flex-wrap items-center gap-2">
                                        <input
                                            value={this.state.editingTitles[release.id]}
                                            onChange={e =>
                                                this.setState(prev => ({
                                                    editingTitles: {
                                                        ...prev.editingTitles,
                                                        [release.id]: e.target.value,
                                                    },
                                                }))
                                            }
                                            className="w-72 max-w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-800 dark:text-white"
                                            placeholder={getString('mobile_apps.app_title_placeholder')}
                                        />
                                        <button
                                            onClick={() => this.saveTitle(release)}
                                            disabled={!!this.state.savingTitles[release.id]}
                                            className="px-2.5 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 text-sm"
                                        >
                                            {this.state.savingTitles[release.id] ? getString('common.loading') : getString('common.save')}
                                        </button>
                                        <button
                                            onClick={() => this.cancelEditTitle(release.id)}
                                            disabled={!!this.state.savingTitles[release.id]}
                                            className="px-2.5 py-1.5 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600 text-sm"
                                        >
                                            {getString('common.cancel')}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap items-center gap-2">
                                        <div className="font-semibold text-gray-900 dark:text-white">{release.title || release.fileName}</div>
                                        <button
                                            onClick={() => this.startEditTitle(release)}
                                            className="px-2 py-1 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600 text-xs"
                                        >
                                            {getString('mobile_apps.edit_title')}
                                        </button>
                                    </div>
                                )}
                                <div className="text-xs text-gray-500 dark:text-gray-400">{release.fileName}</div>
                                <div className="text-xs text-gray-600 dark:text-gray-300">
                                    {release.platform === 'android' ? 'Android APK' : 'iOS Ad Hoc IPA'} · {release.version || '-'} ·{' '}
                                    {formatBytes(release.sizeBytes)}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{new Date(release.createdAt).toLocaleString()}</div>
                                {release.notes && <div className="text-sm text-gray-700 dark:text-gray-200 mt-1">{release.notes}</div>}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <a
                                    href={release.downloadUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="px-3 py-2 rounded bg-gray-700 text-white hover:bg-gray-800 inline-flex items-center gap-1"
                                >
                                    <HiDownload /> {getString('mobile_apps.download')}
                                </a>
                                <button
                                    onClick={() => this.copy(release.downloadUrl)}
                                    className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1"
                                >
                                    <HiClipboardCopy /> {getString('mobile_apps.copy_link')}
                                </button>
                                <button
                                    onClick={() => this.removeRelease(release.id)}
                                    className="px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1"
                                >
                                    <HiTrash /> {getString('common.delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        )
    }
}
